import React from 'react';
import { ArrowRight, BookOpen, DollarSign, Wrench, Users, CheckCircle2, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white" data-testid="how-it-works-page-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="how-it-works-page-heading"
          >
            How NOLA HVAC Connect Works
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            NOLA HVAC Connect connects homeowners with trusted, licensed HVAC professionals
            throughout the Greater New Orleans and Northshore region while providing tools and
            guides to help homeowners make informed HVAC decisions.
          </p>
          <p className="text-base text-slate-600 max-w-2xl mx-auto mb-4">
            Serving homeowners across <strong>New Orleans, Metairie, Kenner, Slidell, Mandeville,
            Covington, Hammond, and the surrounding Northshore communities.</strong>
          </p>
          <p className="text-base text-slate-500 max-w-2xl mx-auto mb-8">
            Our goal is simple: help homeowners understand their options before choosing a contractor.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="hero-cta-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg"
            >
              Get Your Instant HVAC Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Section 1 — Learn About Your HVAC System */}
      <section className="py-16" data-testid="learn-about-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Learn About Your HVAC System
            </h2>
          </div>
          <div className="bg-slate-50 rounded-2xl p-8 mt-6 space-y-4">
            <p className="text-slate-600 text-base leading-relaxed">
              Most homeowners only replace their HVAC system once or twice in their lifetime.
              NOLA HVAC Connect provides easy-to-understand guides explaining how HVAC systems
              work, common repair issues, and the factors that influence repair and replacement
              decisions.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              These resources help homeowners feel more confident before scheduling contractor
              estimates.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2 — Understand HVAC Replacement Pricing */}
      <section className="py-16 bg-slate-50" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Understand HVAC Replacement Pricing
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mt-6 space-y-4">
            <p className="text-slate-600 text-base leading-relaxed">
              HVAC installation costs can vary depending on system size, equipment type,
              installation complexity, and home layout.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              NOLA HVAC Connect provides pricing guides that explain the factors influencing
              HVAC replacement costs so homeowners know what to expect when comparing contractor
              estimates.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 — Explore HVAC Tools and Resources */}
      <section className="py-16" data-testid="tools-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Wrench className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Explore HVAC Tools and Resources
            </h2>
          </div>
          <p className="text-slate-600 text-lg mt-4 mb-6">
            NOLA HVAC Connect provides tools designed to help homeowners better understand
            their HVAC options before contacting a contractor.
          </p>
          <p className="text-slate-600 text-base mb-6">
            These resources may include:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {[
              'Instant HVAC Estimate tools',
              'HVAC sizing guidance',
              'Repair vs replacement guidance',
              'Educational HVAC guides',
            ].map((tool) => (
              <div key={tool} className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" />
                <span className="text-slate-700 text-sm font-medium">{tool}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-base">
            These tools help homeowners approach contractor conversations with more confidence.
          </p>
        </div>
      </section>

      {/* Section 4 — Connect With Trusted Local HVAC Professionals */}
      <section className="py-16 bg-slate-50" data-testid="connect-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Connect With Trusted Local HVAC Professionals
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mt-6 space-y-4">
            <p className="text-slate-600 text-base leading-relaxed">
              When homeowners are ready to move forward, NOLA HVAC Connect helps connect them
              with trusted, licensed HVAC professionals serving the Greater New Orleans and
              Northshore region.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              Our goal is to support homeowners while helping reputable local HVAC companies
              connect with customers who are actively researching their options.
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 — Why Homeowners Use NOLA HVAC Connect */}
      <section className="py-16" data-testid="why-homeowners-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <MapPin className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why Homeowners Use NOLA HVAC Connect
            </h2>
          </div>
          <p className="text-slate-600 text-lg mt-4 mb-6">
            Homeowners often use NOLA HVAC Connect when they want to:
          </p>
          <div className="space-y-3">
            {[
              'Understand HVAC replacement options',
              'Decide whether to repair or replace an AC system',
              'Learn more about HVAC systems before scheduling estimates',
              'Connect with trusted HVAC professionals serving their area',
            ].map((reason) => (
              <div key={reason} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700 text-base">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Start Exploring */}
      <section className="py-20 bg-[#0F172A]" data-testid="explore-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Start Exploring Your HVAC Options
          </h2>
          <p className="text-slate-300 text-lg mb-4">
            Whether you're researching HVAC replacement costs, evaluating repair options, or
            planning a future system upgrade, NOLA HVAC Connect provides the resources to help
            you get started.
          </p>
          <p className="text-slate-400 text-base mb-8">
            Explore our HVAC guides, use our tools, and connect with trusted local professionals
            when you're ready to move forward.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="cta-estimate-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-10 rounded-full text-lg orange-glow transition-all duration-300 transform hover:-translate-y-1"
            >
              Get Your Instant HVAC Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <RelatedArticles currentPath="/how-nola-hvac-connect-works" />

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
